import { isApiError } from "@/hooks/FetchHook";

type RoleMap = Record<string, string>;

/**
 * Short, user-facing detail for a failed insight mutation.
 *
 * The create panel's copy is deliberately generic, which left a repeatable
 * failure ("Create Insight always errors") impossible to triage without the
 * network tab. Carrying the HTTP status distinguishes a rejected payload
 * (400) from a missing trial (404) or an expired session (401/403) at a
 * glance. Returns undefined when there is nothing useful to add.
 */
export const insightErrorDetail = (error: unknown): string | undefined => {
  if (isApiError(error)) return `HTTP ${error.statusCode}`;
  return undefined;
};

export const getUserRoleForTrial = (
  userInfo: { roles?: RoleMap } | undefined,
  trialId?: string,
): string | undefined => {
  if (!userInfo?.roles || !trialId) return undefined;
  return userInfo.roles[trialId];
};

export const ANSWER_EXCERPT_LENGTH = 160;

const TABLE_LINE = /^\s*\|.*\|\s*$/;
const TABLE_SEPARATOR = /^\s*\|?(\s*:?-{3,}:?\s*\|)+\s*(:?-{3,}:?\s*)?$/;

const describeTable = (tableLines: string[]) => {
  const dataRows = Math.max(
    tableLines.filter((line) => !TABLE_SEPARATOR.test(line)).length - 1,
    0,
  );
  return `[table, ${dataRows} ${dataRows === 1 ? "row" : "rows"}]`;
};

const summarizeTables = (markdown: string) => {
  const lines: string[] = [];
  let tableLines: string[] = [];
  const closeTable = () => {
    if (tableLines.length) lines.push(describeTable(tableLines));
    tableLines = [];
  };
  markdown.split("\n").forEach((line) => {
    if (TABLE_LINE.test(line)) {
      tableLines.push(line);
      return;
    }
    closeTable();
    lines.push(line);
  });
  closeTable();
  return lines.join("\n");
};

const MARKDOWN_TO_PLAIN_TEXT: [RegExp, string][] = [
  [/!\[([^\]]*)\]\([^)]*\)/g, "$1"],
  [/\[([^\]]*)\]\([^)]*\)/g, "$1"],
  [/<[^>]+>/g, " "],
  [/^\s{0,3}(#{1,6}|>|[-*+]|\d+[.)])\s+/gm, ""],
  [/(\*\*|__|`)/g, ""],
  [/\|/g, " "],
];

/**
 * A one-glance preview of an answer for a table cell: markdown flattened to
 * its text so a list doesn't show raw syntax, any table reduced to a row
 * count (its cells read as noise in one line), cut on a word boundary.
 */
export const toAnswerExcerpt = (
  answer: string | null | undefined,
  maxLength: number = ANSWER_EXCERPT_LENGTH,
): string => {
  const plainText = MARKDOWN_TO_PLAIN_TEXT.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    summarizeTables(answer ?? ""),
  )
    .replace(/\s+/g, " ")
    .trim();

  if (!plainText) return "No answer recorded.";
  if (plainText.length <= maxLength) return plainText;

  const cut = plainText.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(" ");
  return `${lastSpace > 0 ? cut.slice(0, lastSpace) : cut}…`;
};
